import './DomainDetailPage.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useFilters } from '../../../shared/contexts/filters/FiltersContext';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { useProjectDates } from '../../../shared/hooks/filters/UseProjectDates';
import useFiltersInitializing from '../../../shared/hooks/filters/UseFiltersInitializing';
import { SOURCES } from '../../../shared/routes/path';
import { chatTypeOptions, expandSelectedChatTypes, getMonitorTypesForChatType } from '../../../shared/helpers/MonitorTypeHelpers';
import { ChatType } from '../../../shared/types/monitor/ChatType';
import { getDomainsStats } from '../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import FilterPanel from '../../../shared/components/filter-panel/FilterPanel';
import { FilterItem } from '../../../shared/components/filter-box/models/FilterBoxModel';
import SourceRetrievalChart from './components/source-retrieval-chart/SourceRetrievalChart';
import UrlTypesChart from './components/url-types-chart/UrlTypesChart';
import { useGetSourcesDailyStatsForDomain } from '../../../shared/api/hooks/monitor/data/sources/UseGetSourcesDailyStatsForDomain';
import { useGetUrlTypesStatsForDomain } from '../../../shared/api/hooks/monitor/data/sources/UseGetUrlTypesStatsForDomain';
import { useSourceRetrievalCountMatrix } from './hooks/UseSourceRetrievalCountMatrix';
import { useGetSourcesStatsForDomain } from '../../../shared/api/hooks/monitor/data/sources/UseGetSourcesStatsForDomain';
import DomainSourcesTable from './components/domain-sources-table/DomainSourcesTable';
import BrandMentionsChart from './components/brand-mentions-chart/BrandMentionsChart';
import { isVideoSource, withTagBrandNames } from '../helpers/SourcesHelper';
import PlatformsTable from './components/platforms-table/PlatformsTable';
import { TAG_NAME } from '../../../shared/utils/urls';
import { getTagLabel } from '../../../shared/helpers/tags/TagsHelper';
import { useGetTags } from '../../../shared/api/hooks/tags/useGetTags';
import { usePreviousPeriodDates } from '../hooks/UsePreviousPeriodDates';
import { useGetBrandsStatsForDomain } from '../../../shared/api/hooks/monitor/data/sources/UseGetBrandsStatsForDomain';
import { useGetPlatformStatsForDomain } from '../../../shared/api/hooks/monitor/data/sources/UseGetPlatformStatsForDomain';
import { useGetChatsForDomain } from '../../../shared/api/hooks/monitor/data/sources/UseGetChatsForDomain';
import ChatsForDomainTable from './components/chats-for-domain-table/ChatsForDomainTable';
import { useGetCommonKeywords } from '../../../shared/api/hooks/monitor/keyword/useGetCommonKeywords';
import { useGetMonitorsForProject } from '../../../shared/api/hooks/monitor/useGetMonitorsForProject';
import PromptGroupDropdown from '../../../shared/components/prompt-group-dropdown/PromptGroupDropdown';
import usePromptGroupId, { ALL_PROMPT_GROUPS_ID } from '../../../shared/hooks/filters/UsePromptGroupId';
import MobilePageHeader from '../../../shared/components/common/mobile-menu/MobilePageHeader';
import useIsMobile from '../../../shared/hooks/global/UseIsMobile';

const DomainDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { projectId, domain } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedChatType, selectedChatTypes, monitorPeriod, dateRange } = useFilters();
  const { startDate, endDate } = useProjectDates();
  const { accountResponse, projectResponse } = useAccount();

  const filtersInitializing = useFiltersInitializing();

  const projectIdNumber = Number(projectId);
  const decodedDomain = domain ? decodeURIComponent(domain) : '';
  const { commonKeywords } = useGetCommonKeywords(projectIdNumber);

  const [sourcesPage, setSourcesPage] = useState(1);
  const [sourcesPageSize, setSourcesPageSize] = useState(10);
  const [sourcesKeyword, setSourcesKeyword] = useState('');
  const [sourcesSortBy, setSourcesSortBy] = useState<string>('count');
  const [sourcesSortOrder, setSourcesSortOrder] = useState<string>('DESC');

  const [citationGapsActive, setCitationGapsActive] = useState(false);

  const [chatsPage, setChatsPage] = useState(1);
  const [chatsPageSize, setChatsPageSize] = useState(10);
  const [chatsKeyword, setChatsKeyword] = useState('');

  const [searchParams] = useSearchParams();
  const { promptGroupId } = usePromptGroupId();
  const { monitorPrompts } = useGetMonitorsForProject(projectIdNumber);

  const filteredMonitorPromptIds = useMemo(() => {
    if (!monitorPrompts || promptGroupId === ALL_PROMPT_GROUPS_ID) return undefined;
    return monitorPrompts
      .filter((p) => p.promptGroupId === promptGroupId)
      .map((p) => p.id)
      .filter((id): id is number => id != null);
  }, [monitorPrompts, promptGroupId]);

  const hasExplicitModelFilter = searchParams.get('models') !== null;
  const filterMonitorTypes = useMemo(() => {
    if (!hasExplicitModelFilter || !selectedChatTypes || selectedChatTypes.length === 0) return undefined;
    const expandedChatTypes = expandSelectedChatTypes(selectedChatTypes);
    return Array.from(expandedChatTypes).flatMap(ct => getMonitorTypesForChatType(ct as ChatType));
  }, [hasExplicitModelFilter, selectedChatTypes]);

  const { sourcesDailyStats, isLoading: isDailyStatsLoading } = useGetSourcesDailyStatsForDomain(
    projectIdNumber,
    decodedDomain,
    selectedChatType,
    monitorPeriod,
    startDate,
    endDate,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const sourceRetrievalCountMatrix = useSourceRetrievalCountMatrix(sourcesDailyStats, decodedDomain);

  const { urlTypesStats, isLoading: isUrlTypesLoading } = useGetUrlTypesStatsForDomain(
    projectIdNumber,
    decodedDomain,
    selectedChatType,
    startDate,
    endDate,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const { domainSourcesStats, isLoading: isDomainSourcesLoading } = useGetSourcesStatsForDomain(
    projectIdNumber,
    decodedDomain,
    selectedChatType,
    startDate,
    endDate,
    sourcesPage,
    sourcesPageSize,
    sourcesKeyword || undefined,
    citationGapsActive ? false : undefined,
    sourcesSortBy,
    sourcesSortOrder,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const { domainSourcesStats: citationGapsStats } = useGetSourcesStatsForDomain(
    projectIdNumber,
    decodedDomain,
    selectedChatType,
    startDate,
    endDate,
    1,
    1,
    undefined,
    false,
    undefined,
    undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const previousPeriod = usePreviousPeriodDates(startDate, endDate);

  const { domainSourcesStats: previousDomainSourcesStats } = useGetSourcesStatsForDomain(
    projectIdNumber,
    decodedDomain,
    selectedChatType,
    previousPeriod?.previousStartDate ?? null,
    previousPeriod?.previousEndDate ?? null,
    sourcesPage,
    sourcesPageSize,
    sourcesKeyword || undefined,
    citationGapsActive ? false : undefined,
    sourcesSortBy,
    sourcesSortOrder,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const citationGapsCount = citationGapsStats?.pagination?.totalItems ?? 0;

  const sourcesWithComparison = useMemo(() => {
    if (!domainSourcesStats?.sources) return [];

    const previousShareMap = new Map(
      (previousDomainSourcesStats?.sources ?? []).map((s) => [s.source, s.share])
    );

    return domainSourcesStats.sources.map((source) => ({
      ...source,
      previousShare: previousShareMap.get(source.source) ?? 0,
      shareChange: source.share - (previousShareMap.get(source.source) ?? 0),
    }));
  }, [domainSourcesStats, previousDomainSourcesStats]);

  const { platformStats, isLoading: isPlatformStatsLoading } = useGetPlatformStatsForDomain(
    projectIdNumber,
    decodedDomain,
    selectedChatType,
    startDate,
    endDate,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const { platformStats: previousPlatformStats } = useGetPlatformStatsForDomain(
    projectIdNumber,
    decodedDomain,
    selectedChatType,
    previousPeriod?.previousStartDate ?? null,
    previousPeriod?.previousEndDate ?? null,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const { brandsStats, isLoading: isBrandsStatsLoading } = useGetBrandsStatsForDomain(
    projectIdNumber,
    decodedDomain,
    selectedChatType,
    startDate,
    endDate,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const { chatsForDomain, isLoading: isChatsLoading } = useGetChatsForDomain(
    projectIdNumber,
    decodedDomain,
    selectedChatType,
    startDate,
    endDate,
    chatsPage,
    chatsPageSize,
    chatsKeyword || undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const { tags } = useGetTags(projectIdNumber);
  const brandNames = useMemo(() => withTagBrandNames(commonKeywords, tags), [commonKeywords, tags]);

  const currentProject = useMemo(
    () => projectResponse?.find((p) => p.id === projectIdNumber),
    [projectResponse, projectIdNumber]
  );

  const firmName = useMemo(() => {
    if (!currentProject) return '';
    const rawFirmName = currentProject.firmName || '';
    if (!rawFirmName.startsWith(TAG_NAME)) {
      return rawFirmName;
    }
    const tagId = Number(rawFirmName.slice(TAG_NAME.length)) || null;
    return getTagLabel(tags, tagId, rawFirmName);
  }, [currentProject, tags]);

  const handleSourcesPageChange = (page: number, pageSize: number): void => {
    setSourcesPage(page);
    setSourcesPageSize(pageSize);
  };

  const handleSourcesSort = (field: string, order: string): void => {
    setSourcesSortBy(field);
    setSourcesSortOrder(order);
    setSourcesPage(1);
  };

  const periodFilterItems: FilterItem[] = useMemo(() => {
    if (!selectedChatType || !monitorPeriod) return [];
    const replaceLastTwoSegments = (url: string, period: string, chat: string): string => {
      const parts = url.split('/').filter(Boolean);
      return `/${parts.slice(0, -2).join('/')}/${period}/${chat}`;
    };
    const chat = selectedChatType.toLowerCase();
    return [
      { url: replaceLastTwoSegments(location.pathname, 'daily', chat), label: t('dailyFilter'), isActive: monitorPeriod === 'DAILY' },
      { url: replaceLastTwoSegments(location.pathname, 'weekly', chat), label: t('weeklyFilter'), isActive: monitorPeriod === 'WEEKLY' },
      { url: replaceLastTwoSegments(location.pathname, 'monthly', chat), label: t('monthlyFilter'), isActive: monitorPeriod === 'MONTHLY' },
    ];
  }, [location.pathname, selectedChatType, monitorPeriod, t]);

  const isLockedModel = useMemo(() => {
    if (!accountResponse || !selectedChatType || selectedChatType === 'ALL') return false;
    const available = chatTypeOptions(accountResponse.features);
    return !available.some((opt) => opt.type === selectedChatType);
  }, [accountResponse, selectedChatType]);

  const domainExistsQueryEnabled = !!projectIdNumber && !!decodedDomain && !!dateRange;
  const { data: domainExistsData, isLoading: isDomainExistsLoading } = useQuery({
    queryKey: ['domain-exists-check', projectIdNumber, decodedDomain],
    queryFn: () =>
      getDomainsStats(
        projectIdNumber,
        false,
        'ALL',
        dateRange!.oldestDate,
        dateRange!.newestDate,
        1,
        1,
        [decodedDomain]
      ),
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: domainExistsQueryEnabled,
  });

  const domainNotFound =
    domainExistsQueryEnabled && !isDomainExistsLoading && domainExistsData?.domains?.length === 0;

  const sourcesUrl = `${SOURCES}/${projectId}/${monitorPeriod?.toLowerCase() || 'daily'}/${selectedChatType?.toLowerCase() || 'all'}${location.search}`;

  useEffect(() => {
    if (!accountResponse || !projectId) return;
    if (isLockedModel || domainNotFound) {
      navigate(sourcesUrl, { replace: true });
    }
  }, [accountResponse, projectId, isLockedModel, domainNotFound, navigate, sourcesUrl]);

  if (!accountResponse || !projectId || isLockedModel || domainNotFound) {
    return <></>;
  }

  return (
    <div className="domain-detail-page page-content">
      <div className="page-box">
        {/* Telefon: domena (z favikoną) i link do niej w górnym pasku, filtry pod nim (jak na Źródłach). */}
        <MobilePageHeader
          title={
            <>
              <span
                className="source-data__icon source-data__icon--title"
                style={{ backgroundImage: `url("https://favicon.is/${decodedDomain}")` }}
              />
              <span>{decodedDomain}</span>
            </>
          }
          actions={
            <a
              href={`https://${decodedDomain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mobile-header__icon-btn"
              aria-label={decodedDomain}
            >
              <span className="ico--sm ico--external-link" />
            </a>
          }
        />
        {isMobile ? (
          <div className="mobile-page-filters">
            <FilterPanel baseUrl={location.pathname} showPeriodTabs={false}>
              <PromptGroupDropdown projectId={projectIdNumber} />
            </FilterPanel>
          </div>
        ) : (
        <div className="page-box__filters" data-observe-sticky>
          <div className="title-box">
            <div className="title-box__title">
              <div className="title-box__nav">
                <nav className="breadcrumbs">
                  <ul>
                    <li>
                      <Link to={sourcesUrl}>{t('sources')}</Link>
                    </li>
                    <li>
                      <span>{t('domains')}</span>
                    </li>
                  </ul>
                </nav>
                <div className="title-box__row">
                  <div className="title-box__row-title">
                    <span
                      className="source-data__icon source-data__icon--title"
                      style={{ backgroundImage: `url("https://favicon.is/${decodedDomain}")` }}
                    />
                    {decodedDomain}
                    <a href={`https://${decodedDomain}`} target="_blank" rel="noopener noreferrer" className="domain-detail-page__external-link">
                      <span className="ico--sm ico--external-link" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="title-box__action">
              <div className="filters">
                <FilterPanel baseUrl={location.pathname} showPeriodTabs={false}>
                  <PromptGroupDropdown projectId={projectIdNumber} />
                </FilterPanel>
              </div>
            </div>
          </div>
        </div>
        )}
        <div className="page-box__stats">
          <div className="stats stats--full-width">
            <SourceRetrievalChart countMatrix={sourceRetrievalCountMatrix} isLoading={isDailyStatsLoading || filtersInitializing} periodFilterItems={periodFilterItems} />
          </div>
          <div className="stats stats--full-width">
            <UrlTypesChart urlTypesStats={urlTypesStats} isLoading={isUrlTypesLoading || filtersInitializing} />
          </div>
          <div className="stats stats--full-width">
            <DomainSourcesTable
              sources={sourcesWithComparison}
              pagination={domainSourcesStats?.pagination}
              onPageChange={handleSourcesPageChange}
              isLoading={isDomainSourcesLoading || filtersInitializing}
              keyword={sourcesKeyword}
              onKeywordChange={(kw) => {
                setSourcesKeyword(kw);
                setSourcesPage(1);
              }}
              firmName={firmName}
              tags={tags}
              citationGapsActive={citationGapsActive}
              citationGapsCount={citationGapsCount}
              onCitationGapsToggle={() => {
                setCitationGapsActive((prev) => !prev);
                setSourcesPage(1);
              }}
              projectId={projectIdNumber}
              monitorPeriod={monitorPeriod}
              selectedChatType={selectedChatType}
              hasSourceDetailPage={true}
              commonKeywords={brandNames}
              sortBy={sourcesSortBy}
              sortOrder={sourcesSortOrder}
              onSort={handleSourcesSort}
              hasAdvancedFeatures={accountResponse.addons.includes('SOURCES_STATS_ADVANCED')}
            />
          </div>
          {accountResponse.addons.includes('SOURCES_STATS_ADVANCED') &&
            !isVideoSource(decodedDomain) &&
            (isBrandsStatsLoading || (brandsStats?.brands?.length ?? 0) > 0) && (
              <div className="stats stats--full-width">
                <BrandMentionsChart
                  brandsStats={brandsStats}
                  isLoading={isBrandsStatsLoading || filtersInitializing}
                  commonKeywords={brandNames}
                />
              </div>
            )}
          <div className="stats stats--full-width">
            <ChatsForDomainTable
              chats={chatsForDomain?.chats ?? []}
              pagination={chatsForDomain?.pagination}
              onPageChange={(page, pageSize) => {
                setChatsPage(page);
                setChatsPageSize(pageSize);
              }}
              isLoading={isChatsLoading || filtersInitializing}
              keyword={chatsKeyword}
              onKeywordChange={(kw) => {
                setChatsKeyword(kw);
                setChatsPage(1);
              }}
              commonKeywords={brandNames}
              projectId={projectIdNumber}
              hasAdvancedFeatures={accountResponse.addons.includes('SOURCES_STATS_ADVANCED')}
            />
          </div>
          <div className="stats stats--full-width">
            <PlatformsTable
              platformStats={platformStats}
              previousPlatformStats={previousPlatformStats}
              isLoading={isPlatformStatsLoading || filtersInitializing}
              features={accountResponse.features}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DomainDetailPage;
